import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import ConfirmModal from '../../components/modal/confirmModal';
import { useInternals } from '../../context/internalContext';
import { useUsers } from '../../context/usersContext';
import { useUnits } from '../../context/unitsContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faBan } from '@fortawesome/free-solid-svg-icons';
import ReactPaginate from 'react-paginate';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function AgendamentosAdvogados() {
  const { listSchedule, updateSchedule } = useInternals();
  const { buildUnitReadParams } = useUsers();
  const { summaryListUnit } = useUnits();

  const [appointments, setAppointments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [units, setUnits] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [unitId, setUnitId] = useState('');
  const [virtualFilter, setVirtualFilter] = useState('');
  const [internalNameFilter, setInternalNameFilter] = useState('');

  const [selectedToAuthorize, setSelectedToAuthorize] = useState(null);
  const [selectedToCancel, setSelectedToCancel] = useState(null);
  const [reasonInactivation, setReasonInactivation] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    summaryListUnit().then((res) => {
      const list = Array.isArray(res) ? res : res?.message || res?.results || [];
      setUnits(list);
    }).catch(() => {});
  }, [summaryListUnit]);

  const fetchAppointments = useCallback(async () => {
    try {
      const params = buildUnitReadParams({
        limit: pageSize,
        offset: currentPage * pageSize,
        type_schedule_name: 'ADVOGADO(A)',
      });

      if (statusFilter === 'pendente') params.status = 2;
      else if (statusFilter === 'agendado') params.status = 1;
      else if (statusFilter === 'atendido') params.done = true;
      else if (statusFilter === 'cancelado') params.active = false;

      if (startDate) params.start = startDate;
      if (endDate) params.end = endDate;
      if (unitId) params.unit = Number(unitId);
      if (virtualFilter === 'true') params.virtual = true;
      else if (virtualFilter === 'false') params.virtual = false;

      const response = await listSchedule(params);
      setAppointments(Array.isArray(response?.results) ? response.results : []);
      setTotalItems(response?.count ?? 0);
    } catch {
      toast.error('Erro ao buscar agendamentos de advogados.');
    }
  }, [listSchedule, buildUnitReadParams, statusFilter, startDate, endDate, unitId, virtualFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const pageCount = Math.ceil(totalItems / pageSize) || 0;

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(0);
  };

  const getStatusBadge = (appointment) => {
    if (!appointment.active && appointment.reason_inactivation)
      return { label: 'CANCELADO', className: 'status-canceled' };
    if (appointment.done)
      return { label: 'ATENDIDO', className: 'status-done' };
    if (appointment.deadline_status === 'Vencido')
      return { label: 'VENCIDO', className: 'status-expired' };
    if (appointment.deadline_status === 'Vencendo')
      return { label: 'VENCENDO', className: 'status-warning' };
    if (appointment.status_name === 'SOLICITADO')
      return { label: 'SOLICITADO', className: 'status-pending' };
    return { label: appointment.status_name || 'AGENDADO', className: 'status-active' };
  };

  const displayedAppointments =
    internalNameFilter.length >= 3
      ? appointments.filter((a) =>
          a.internal_name?.toLowerCase().includes(internalNameFilter.toLowerCase())
        )
      : appointments;

  const handleAuthorize = async () => {
    if (!selectedToAuthorize) return;
    setIsLoading(true);
    try {
      await updateSchedule(selectedToAuthorize.id, { status: 1 });
      toast.success('Agendamento autorizado com sucesso!');
      setSelectedToAuthorize(null);
      fetchAppointments();
    } catch {
      toast.error('Erro ao autorizar agendamento.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedToCancel) return;
    if (!reasonInactivation.trim()) {
      toast.warn('Por favor, insira um motivo para o cancelamento.');
      return;
    }
    if (reasonInactivation.trim().length < 10) {
      toast.warn('O motivo do cancelamento deve ter pelo menos 10 caracteres.');
      return;
    }
    setIsLoading(true);
    try {
      await updateSchedule(selectedToCancel.id, {
        active: false,
        reason_inactivation: reasonInactivation.trim(),
      });
      toast.success('Agendamento cancelado com sucesso!');
      setReasonInactivation('');
      setSelectedToCancel(null);
      fetchAppointments();
    } catch {
      toast.error('Erro ao cancelar agendamento.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container">
        <Titulo children={<h2>Agendamentos de Advogados</h2>} />

        <div className="filters-container" style={{ marginBottom: '16px' }}>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={handleFilterChange(setStatusFilter)}
          >
            <option value="">TODOS OS STATUS</option>
            <option value="pendente">SOLICITADO</option>
            <option value="agendado">AUTORIZADO</option>
            <option value="atendido">ATENDIDO</option>
            <option value="cancelado">CANCELADO</option>
          </select>

          <select
            className="filter-select"
            value={unitId}
            onChange={handleFilterChange(setUnitId)}
          >
            <option value="">Todas as unidades</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={virtualFilter}
            onChange={handleFilterChange(setVirtualFilter)}
          >
            <option value="">Presencial e Virtual</option>
            <option value="false">Presencial</option>
            <option value="true">Virtual</option>
          </select>

          <input
            className="filter-input"
            type="text"
            value={internalNameFilter}
            onChange={(e) => setInternalNameFilter(e.target.value)}
            placeholder="Nome do interno (mín. 3 letras)"
          />

          <div className="filter-date-range">
            <input
              className="filter-date"
              type="date"
              value={startDate}
              onChange={handleFilterChange(setStartDate)}
              title="Data inicial"
            />
            <input
              className="filter-date"
              type="date"
              value={endDate}
              onChange={handleFilterChange(setEndDate)}
              title="Data final"
            />
          </div>
        </div>

        <div className="content">
          {displayedAppointments.length > 0 ? (
            <div className="appointments-grid">
              {displayedAppointments.map((appointment) => {
                const status = getStatusBadge(appointment);
                const canAuthorize =
                  appointment.active && appointment.status_name === 'SOLICITADO' && !appointment.done;
                const isAuthorized =
                  appointment.active && appointment.status_name !== 'SOLICITADO' && !appointment.done;
                const canCancel = appointment.active && !appointment.done;

                return (
                  <div key={appointment.id} className="appointment-card">
                    <div className="adv-card-header">
                      <div className="adv-card-badge-row">
                        <span className={`appointment-status-badge ${status.className}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="adv-card-header-info">
                        <p className="adv-card-internal-name">{appointment.internal_name || 'N/A'}</p>
                        <p className="adv-card-unit-name">{appointment.unit || 'N/A'}</p>
                        <div className="adv-card-lawyer-block">
                          <span className="adv-card-lawyer-label">Advogado</span>
                          <span className="adv-card-lawyer-name">{appointment.user_name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="appointment-card-info">
                      <div className="appointment-info-item">
                        <strong>Data:</strong>
                        <span>{new Date(appointment.start_date).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="appointment-info-item">
                        <strong>Horário:</strong>
                        <span>
                          {new Date(appointment.start_date).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <div className="appointment-info-item">
                        <strong>Tipo:</strong>
                        <span className={appointment.virtual ? 'tag-virtual' : 'tag-presencial'}>
                          {appointment.virtual ? 'Virtual' : 'Presencial'}
                        </span>
                      </div>
                      {appointment.observation && (
                        <div className="adv-observation">
                          <strong>Observação</strong>
                          <p>{appointment.observation}</p>
                        </div>
                      )}
                      {appointment.reason_inactivation && (
                        <div className="adv-cancel-reason">
                          <strong>Motivo Cancelamento</strong>
                          <p>{appointment.reason_inactivation}</p>
                        </div>
                      )}
                    </div>
                    <div className="appointment-card-footer">
                      {canAuthorize && (
                        <button
                          className="btn-action-complete"
                          title="Autorizar agendamento"
                          onClick={() => setSelectedToAuthorize(appointment)}
                          disabled={isLoading}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} /> Autorizar
                        </button>
                      )}
                      {isAuthorized && (
                        <span className="btn-authorized-label">
                          <FontAwesomeIcon icon={faCheckCircle} /> Autorizado
                        </span>
                      )}
                      <button
                        className="btn-action-delete"
                        title="Cancelar agendamento"
                        onClick={() => setSelectedToCancel(appointment)}
                        disabled={!canCancel || isLoading}
                      >
                        <FontAwesomeIcon icon={faBan} /> Cancelar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="no-appointments">Nenhum agendamento encontrado.</p>
          )}
        </div>

        <div className="pagination-options">
          <div>
            <label>Itens por página</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          {pageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={pageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentPage}
            />
          )}
        </div>

        <ConfirmModal
          isOpen={!!selectedToAuthorize}
          closeModal={() => setSelectedToAuthorize(null)}
          onConfirm={handleAuthorize}
          icon={faCheckCircle}
          mainMessage="Confirmar Autorização"
          secondaryMessage="Deseja autorizar este agendamento?"
          loading={isLoading}
        />

        <ConfirmModal
          isOpen={!!selectedToCancel}
          closeModal={() => {
            setSelectedToCancel(null);
            setReasonInactivation('');
          }}
          onConfirm={handleCancel}
          icon={faBan}
          mainMessage="Cancelar Agendamento"
          secondaryMessage="Essa ação não pode ser desfeita."
          loading={isLoading}
        >
          <div style={{ marginTop: '20px' }}>
            <label htmlFor="cancel-reason-duap">Motivo do Cancelamento:</label>
            <textarea
              id="cancel-reason-duap"
              value={reasonInactivation}
              onChange={(e) => setReasonInactivation(e.target.value)}
              placeholder="Digite o motivo do cancelamento (mínimo 10 caracteres)"
              required
              rows="4"
              disabled={isLoading}
            />
          </div>
        </ConfirmModal>
      </div>
    </div>
  );
}

export default AgendamentosAdvogados;
